import { Stack } from '@mui/system';
import {
  Card,
  CardContent,
  CardMedia,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';

import { CustomAvatar } from '@app/components/custom-avatar';
import { Strings } from '@app/constants';
import { SvgColor } from '../svg-color';
import { StatusTag } from '../status-tag';
import { Scrollbar } from '../scrollbar';
import { ReviewDetailsCard } from './review-details-card';

interface Props {
  title?: string;
  fields: string[];
  data: Array<string | number | boolean | ReactNode | undefined>;
  Src?: string;
  desc?: string;
  columnCount?: number;
  height?: string | number;
  iconImg?: string;
  ratings?: Array<{ heading: string; iconImg: string; value: string }>;
}

const ProfileCard = ({
  title,
  fields,
  data,
  Src,
  height,
  columnCount,
  desc,
  iconImg,
  ratings,
}: Props) => {
  const theme = useTheme();

  return (
    <Card sx={{ margin: 1, height }}>
      {title && (
        <Stack
          sx={{
            borderRadius: '0px 0px 10px 10px',
            bgcolor: theme.palette.info.lighter,
            ml: '60px',
            mr: '60px',
            width: '200px',
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={13}
            sx={{
              padding: '5px',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Stack>
      )}
      <Grid
        display="flex"
        flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
        alignItems={{ sm: 'center' }}
      >
        {Src ? (
          <Grid item xs={12} md={4}>
            <CardMedia
              sx={{
                marginLeft: '50px',
              }}
            >
              <Stack
                sx={{
                  width: 220,
                  height: 220,
                  top: 171,
                  left: 432,
                  mt: '25px',
                  mb: '25px',
                  border: '2px dashed #D8DBDE',
                  borderStyle: 'dashed',
                  borderRadius: '50%',
                }}
              >
                <CustomAvatar
                  src={Src}
                  alt="profile image"
                  sx={{
                    width: 200,
                    height: 200,
                    backgroundColor:
                      typeof Src === 'string' && Src.startsWith('http')
                        ? theme.palette.background.neutral
                        : theme.palette.primary.main,
                    alignSelf: 'center',
                    fontSize: '36px',
                    color: theme.palette.common.white,
                    mt: '8px',
                    '& img': {
                      objectFit: 'cover',
                    },
                  }}
                  name={Src}
                />
              </Stack>
            </CardMedia>
          </Grid>
        ) : (
          iconImg && (
            <Grid item xs={12} md={4}>
              <CardMedia
                sx={{
                  marginLeft: '50px',
                }}
              >
                <Stack
                  sx={{
                    width: 220,
                    height: 220,
                    top: 171,
                    left: 432,
                    mt: '25px',
                    mb: '25px',
                    alignItems: 'center',
                    border: `2px dashed ${theme.palette.taskboard.aliceBlue}`,
                    borderStyle: 'dashed',
                    borderRadius: '50%',
                    justifyContent: 'center',
                  }}
                >
                  <Stack
                    sx={{
                      bgcolor: theme.palette.info.main,
                      width: 200,
                      height: 300,
                      alignItems: 'center',
                      borderRadius: '50%',
                      mt: '10px',
                      mb: '10px',
                    }}
                  >
                    <SvgColor
                      src={`/assets/icons/card/${iconImg}.svg`}
                      sx={{
                        width: '150px',
                        height: '150px',
                        color: theme.palette.common.white,
                        mt: '17px',
                      }}
                    />
                  </Stack>
                </Stack>
              </CardMedia>
            </Grid>
          )
        )}
        <Grid
          item
          xs={12}
          alignContent="center"
          marginLeft={{ sm: '10px', md: '50px' }}
          width={{ sm: '80%', md: '80%' }}
        >
          <CardContent sx={{ columnCount }}>
            <Table size="small">
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        borderBottom: 'none',
                        verticalAlign: 'middle',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        width: '30%',
                      }}
                    >
                      {field}:
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: 'none',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        verticalAlign: 'middle',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        width: '70%',
                      }}
                    >
                      {(field === Strings.assets.status &&
                        (data[index] as { key: string })?.key !== 'taskStatus') ||
                      field === Strings.field.priority ||
                      field === Strings.reviews.overallRating ? (
                        <StatusTag status={data[index] as string} />
                      ) : (
                        data[index]
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Grid>
      </Grid>
      {ratings && (
        <>
          <Divider />
          <Scrollbar>
            <Stack m={2} ml={3}>
              <Typography variant="subtitle1" mb={1}>
                {Strings.reviews.ratings}
              </Typography>
              <Stack
                sx={{ flexWrap: 'wrap', gap: 1.4 }}
                flexDirection={{ sm: 'column', md: 'row' }}
              >
                {ratings.map((rating: any, index: any) => (
                  <ReviewDetailsCard
                    key={index}
                    title={rating.heading}
                    ratingData={rating.value ? [rating.value] : ['N/A']}
                    height={'90px'}
                    ratingIconImg={rating.iconImg}
                    width={{ md: '19%', sm: '80%' }}
                  />
                ))}
              </Stack>
            </Stack>
          </Scrollbar>
        </>
      )}

      {desc && (
        <>
          <Divider />
          <Stack m={3} spacing={1}>
            <Typography variant="subtitle1">{Strings.assets.description}</Typography>
            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: desc || '' }} />
          </Stack>
        </>
      )}
    </Card>
  );
};

export { ProfileCard };
